import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, Edit3, Languages, Plus, Trash2, X } from 'lucide-react';

import { translations as builtInTranslations, useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../lib/api';

const emptyTranslation = {
  key: '',
  english: '',
  chinese: '',
  category: 'general',
  is_active: true
};

function authHeaders() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

function categoryForKey(key) {
  if (['app_name', 'loading', 'save', 'cancel', 'delete', 'edit', 'back', 'next', 'submit', 'logout', 'settings'].includes(key)) return 'common';
  if (['login', 'register', 'email', 'password', 'name', 'confirm_password', 'login_title', 'login_subtitle', 'register_title', 'register_subtitle', 'no_account', 'have_account'].includes(key)) return 'auth';
  if (['dashboard', 'welcome_back', 'choose_subject', 'your_progress', 'total_points', 'current_level', 'time_spent', 'quizzes_done', 'continue_learning'].includes(key)) return 'dashboard';
  if (['subjects', 'bm', 'history', 'science'].includes(key)) return 'subjects';
  if (['levels', 'level', 'determination', 'discipline', 'perseverance', 'hardworking', 'breakthrough', 'locked', 'unlocked', 'unlock_at', 'points'].includes(key)) return 'levels';
  if (['stages', 'stage', 'completed', 'start_stage'].includes(key)) return 'stages';
  if (['quiz', 'question', 'time_remaining', 'submit_answers'].includes(key)) return 'quiz';
  if (['results', 'congratulations', 'your_score', 'points_earned', 'time_taken', 'correct_answers', 'try_again', 'next_stage', 'back_to_levels', 'level_up'].includes(key)) return 'results';
  if (['leaderboard', 'rank', 'player', 'global_ranking'].includes(key)) return 'leaderboard';
  if (['history_title', 'no_history', 'date', 'score'].includes(key)) return 'history';
  if (['notices', 'announcements', 'no_notices'].includes(key)) return 'notices';
  if (['admin_panel', 'manage_questions', 'manage_users', 'manage_notices', 'reports', 'upload_questions', 'add_question', 'bulk_upload'].includes(key)) return 'admin';
  if (['language', 'change_password', 'current_password', 'new_password'].includes(key)) return 'settings';
  if (['live_competition', 'host_room', 'join_by_code', 'quick_match', 'tournaments_label', 'room_code', 'create_room', 'start_game'].includes(key)) return 'live';
  return 'misc';
}

const builtInPairs = Object.keys(builtInTranslations.en || {}).map((key) => ({
  id: `builtin-${key}`,
  key,
  english: builtInTranslations.en[key] || '',
  chinese: builtInTranslations.zh?.[key] || '',
  category: categoryForKey(key),
  is_active: true,
  is_builtin: true
}));

function ManageTranslations() {
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [formData, setFormData] = useState(emptyTranslation);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const copy = useMemo(() => {
    const zh = language === 'zh';
    return {
      title: zh ? '切换语言' : 'Switch Language',
      subtitle: zh ? '管理 English = Chinese 的文字对照' : 'Manage English = Chinese text pairs',
      add: zh ? '添加文字' : 'Add Text',
      edit: zh ? '编辑文字' : 'Edit Text',
      empty: zh ? '暂无语言文字' : 'No language pairs yet',
      loading: zh ? '加载中...' : 'Loading...',
      key: zh ? '键名' : 'Key',
      category: zh ? '分类' : 'Category',
      english: 'English',
      chinese: 'Chinese',
      active: zh ? '启用' : 'Active',
      inactive: zh ? '停用' : 'Inactive',
      cancel: zh ? '取消' : 'Cancel',
      save: zh ? '保存' : 'Save',
      update: zh ? '更新' : 'Update',
      deleteConfirm: zh ? '删除这组文字？' : 'Delete this language pair?',
      loadError: zh ? '无法加载语言文字' : 'Failed to load language pairs',
      saveError: zh ? '无法保存语言文字' : 'Failed to save language pair',
      deleteError: zh ? '无法删除语言文字' : 'Failed to delete language pair',
      saved: zh ? '语言文字已保存' : 'Language pair saved',
      deleted: zh ? '语言文字已删除' : 'Language pair deleted'
    };
  }, [language]);

  const visibleTranslations = useMemo(() => {
    const customByKey = new Map(translations.map((item) => [item.key, item]));
    const mergedBuiltIns = builtInPairs.map((item) => {
      const custom = customByKey.get(item.key);
      if (!custom) return item;
      return { ...item, ...custom, is_builtin: true, is_override: true };
    });
    const customOnly = translations
      .filter((item) => !builtInTranslations.en?.[item.key])
      .map((item) => ({ ...item, is_builtin: false }));

    return [...mergedBuiltIns, ...customOnly].sort((a, b) => (
      String(a.category || '').localeCompare(String(b.category || '')) ||
      String(a.key || '').localeCompare(String(b.key || ''))
    ));
  }, [translations]);

  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/translations`, authHeaders());
      setTranslations(response.data || []);
    } catch (error) {
      toast.error(copy.loadError);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setEditingTranslation(null);
    setFormData(emptyTranslation);
    setShowForm(true);
  };

  const openEditForm = (translation) => {
    setEditingTranslation(translation);
    setFormData({
      key: translation.key || '',
      english: translation.english || '',
      chinese: translation.chinese || '',
      category: translation.category || 'general',
      is_active: translation.is_active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...formData,
      key: formData.key.trim(),
      english: formData.english.trim(),
      chinese: formData.chinese.trim(),
      category: formData.category.trim() || 'general'
    };

    try {
      if (editingTranslation?.id && !String(editingTranslation.id).startsWith('builtin-')) {
        await axios.put(
          `${API_URL}/api/admin/translations/${editingTranslation.id}`,
          payload,
          authHeaders()
        );
      } else {
        await axios.post(`${API_URL}/api/admin/translations`, payload, authHeaders());
      }
      toast.success(copy.saved);
      setShowForm(false);
      await fetchTranslations();
    } catch (error) {
      toast.error(error.response?.data?.detail || copy.saveError);
    }
  };

  const handleDelete = async (translationId) => {
    if (String(translationId).startsWith('builtin-')) return;
    if (!window.confirm(copy.deleteConfirm)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/translations/${translationId}`, authHeaders());
      toast.success(copy.deleted);
      await fetchTranslations();
    } catch (error) {
      toast.error(copy.deleteError);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50" data-testid="manage-translations">
      <header className="border-b-2 border-zinc-200 bg-white/80 px-4 py-6 backdrop-blur-md">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-4 flex items-center gap-2 font-medium text-zinc-600 hover:text-zinc-900"
            data-testid="translations-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            {t('back')}
          </button>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-black text-zinc-900">{copy.title}</h1>
              <p className="mt-1 text-sm font-medium text-zinc-500">{copy.subtitle}</p>
            </div>
            <button
              onClick={openAddForm}
              className="flex items-center gap-2 rounded-xl bg-violet-500 px-4 py-2 font-bold text-white hover:bg-violet-600"
              data-testid="add-translation-btn"
            >
              <Plus className="h-5 w-5" />
              {copy.add}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {loading ? (
          <div className="py-12 text-center text-zinc-500">{copy.loading}</div>
        ) : visibleTranslations.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-zinc-200 bg-white py-12 text-center text-zinc-500">
            {copy.empty}
          </div>
        ) : (
          <div className="grid gap-3">
            <div className="text-sm font-bold text-zinc-500">
              {language === 'zh' ? '全部文字对照' : 'All language pairs'}: {visibleTranslations.length}
            </div>
            {visibleTranslations.map((translation, index) => (
              <motion.div
                key={translation.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="rounded-xl border-2 border-zinc-200 bg-white p-4"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100">
                        <Languages className="h-5 w-5 text-violet-600" />
                      </div>
                      <h2 className="text-lg font-black text-zinc-900">{translation.key}</h2>
                      <span className="rounded-full bg-zinc-100 px-2 py-1 text-xs font-black text-zinc-500">
                        {translation.category || 'general'}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs font-black ${
                        translation.is_active === false
                          ? 'bg-zinc-100 text-zinc-500'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {translation.is_active === false ? copy.inactive : copy.active}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs font-black ${
                        translation.is_builtin ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {translation.is_builtin
                          ? (language === 'zh' ? '内置' : 'Built-in')
                          : (language === 'zh' ? '自订' : 'Custom')}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div className="rounded-lg bg-blue-50 p-3">
                        <p className="text-xs font-black uppercase text-blue-500">{copy.english}</p>
                        <p className="mt-1 text-sm font-bold text-zinc-800">{translation.english}</p>
                      </div>
                      <div className="rounded-lg bg-pink-50 p-3">
                        <p className="text-xs font-black uppercase text-pink-500">{copy.chinese}</p>
                        <p className="mt-1 text-sm font-bold text-zinc-800">{translation.chinese}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditForm(translation)}
                      className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                      title={copy.edit}
                    >
                      <Edit3 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(translation.id)}
                      disabled={translation.is_builtin && !translation.is_override}
                      className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6"
          >
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="text-2xl font-black text-zinc-900">
                {editingTranslation ? copy.edit : copy.add}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100"
                title={copy.cancel}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-bold text-zinc-700">{copy.key}</label>
                  <input
                    value={formData.key}
                    onChange={(event) => setFormData({ ...formData, key: event.target.value })}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2"
                    placeholder="login_title"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-bold text-zinc-700">{copy.category}</label>
                  <input
                    value={formData.category}
                    onChange={(event) => setFormData({ ...formData, category: event.target.value })}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2"
                    placeholder="admin"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-bold text-zinc-700">{copy.english}</label>
                  <textarea
                    value={formData.english}
                    onChange={(event) => setFormData({ ...formData, english: event.target.value })}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2"
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-bold text-zinc-700">{copy.chinese}</label>
                  <textarea
                    value={formData.chinese}
                    onChange={(event) => setFormData({ ...formData, chinese: event.target.value })}
                    className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2"
                    rows={4}
                    required
                  />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm font-bold text-zinc-700">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(event) => setFormData({ ...formData, is_active: event.target.checked })}
                  className="h-5 w-5 rounded border-zinc-300 text-violet-600"
                />
                {copy.active}
              </label>

              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 rounded-xl border-2 border-zinc-200 py-3 font-bold hover:bg-zinc-50"
                >
                  {copy.cancel}
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-violet-500 py-3 font-bold text-white hover:bg-violet-600"
                >
                  {editingTranslation ? copy.update : copy.save}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default ManageTranslations;
